import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import {
  MAX_AVATAR_BYTES,
  MAX_BIO_LENGTH,
  MAX_NAME_LENGTH,
  MAX_STATUS_LENGTH,
  ROLE_SWATCHES,
  formatBytes,
  nameError,
} from "@shared/protocol";
import { C, FONT_MONO, FONT_SANS, hexA, initialOf } from "@/theme";
import { useApp } from "@/state/store";
import { CloseIcon } from "@/components/icons";

const MONO_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
};

const FIELD: CSSProperties = {
  padding: "10px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  background: C.field,
  color: C.text,
  fontSize: 14,
  outline: 0,
};

/**
 * As mensagens de `@shared/protocol` sao escritas sem acento (o contrato serve
 * cliente e servidor); a interface precisa delas acentuadas.
 */
function nameMessage(raw: string): string | null {
  if (!nameError(raw)) return null;
  if (!raw.trim()) return "Escolha como você quer aparecer.";
  return `No máximo ${MAX_NAME_LENGTH} caracteres.`;
}

/** Camera com um sinal de mais: trocar a foto. */
function CameraIcon({ size = 18 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5h3.2L8 6h5.5l1.8 2.5H21v10H3v-10z" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  );
}

export function ProfileEditor() {
  const { state, actions } = useApp();
  const me = state.me;
  const open = state.profileEditorOpen;
  const { closeProfileEditor } = actions;

  const [nameDraft, setNameDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [bioDraft, setBioDraft] = useState("");
  const [colorDraft, setColorDraft] = useState<string | null>(null);
  const [avatarDraft, setAvatarDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [broken, setBroken] = useState(false);
  /** Ponteiro ou foco sobre a foto: e quando o icone de camera aparece. */
  const [avatarActive, setAvatarActive] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // O membro muda de identidade a cada presenca; guardar em ref evita que os
  // rascunhos sejam reescritos no meio da digitacao.
  const meRef = useRef(me);
  useEffect(() => {
    meRef.current = me;
  });

  useEffect(() => {
    if (!open) return;
    const current = meRef.current;
    setNameDraft(current?.name ?? "");
    setStatusDraft(current?.customStatus ?? "");
    setBioDraft(current?.bio ?? "");
    setColorDraft(current?.color ?? null);
    setAvatarDraft(current?.avatarUrl ?? null);
    setBusy(false);
    setError(null);
    setDragOver(false);
    setBroken(false);
    setAvatarActive(false);
  }, [open]);

  useEffect(() => {
    setBroken(false);
  }, [avatarDraft]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeProfileEditor();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeProfileEditor, open]);

  if (!open || !me) return null;

  const color = colorDraft ?? state.settings.accent;
  const problem = nameMessage(nameDraft);

  const receive = async (file: File | undefined): Promise<void> => {
    // Um envio de cada vez: dois arquivos em corrida deixariam no perfil a foto
    // que terminasse por ultimo, que nao e necessariamente a ultima escolhida.
    if (!file || busy) return;
    if (!file.type.startsWith("image/")) {
      setError("Escolha um arquivo de imagem.");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError(`A imagem precisa ter no máximo ${formatBytes(MAX_AVATAR_BYTES)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setAvatarDraft(await actions.uploadImage(file));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Não foi possível enviar a imagem.");
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (busy) return;
    if (problem) {
      setError(problem);
      return;
    }
    const cleanStatus = statusDraft.trim().slice(0, MAX_STATUS_LENGTH);
    actions.updateProfile({
      name: nameDraft.trim(),
      bio: bioDraft.trim().slice(0, MAX_BIO_LENGTH),
      color: colorDraft,
      avatarUrl: avatarDraft,
      customStatus: cleanStatus || null,
    });
    closeProfileEditor();
  };

  const swatchRing = (selected: boolean): string => (selected ? C.text : "transparent");

  return (
    <div
      onClick={closeProfileEditor}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
        animation: "fadein 150ms ease",
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: 380,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          background: "#1d1d1d",
          animation: "pop 200ms ease",
        }}
      >
        <div style={{ position: "relative", height: 74, background: hexA(color, 0.22) }}>
          <button
            type="button"
            onClick={closeProfileEditor}
            title="Fechar"
            className="hov-bg-26-f2"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 30,
              height: 30,
              display: "grid",
              placeItems: "center",
              padding: 0,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 999,
              cursor: "pointer",
              background: "transparent",
              color: C.textMuted,
            }}
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div style={{ padding: "0 20px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginTop: -30 }}>
            <div
              role="button"
              tabIndex={0}
              title="Trocar a foto"
              onClick={() => fileRef.current?.click()}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  fileRef.current?.click();
                }
              }}
              onMouseEnter={() => setAvatarActive(true)}
              onMouseLeave={() => setAvatarActive(false)}
              onFocus={() => setAvatarActive(true)}
              onBlur={() => setAvatarActive(false)}
              onDragOver={event => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={event => {
                event.preventDefault();
                setDragOver(false);
                void receive(event.dataTransfer.files[0]);
              }}
              style={{
                position: "relative",
                width: 68,
                height: 68,
                flex: "0 0 auto",
                display: "grid",
                placeItems: "center",
                boxSizing: "border-box",
                borderRadius: "50%",
                border: `4px solid ${dragOver ? "var(--ac, #1F4FD8)" : "#1d1d1d"}`,
                overflow: "hidden",
                cursor: "pointer",
                background: hexA(color, 0.18),
                color,
                fontFamily: FONT_SANS,
                fontSize: 26,
                fontWeight: 700,
              }}
            >
              {avatarDraft && !broken ? (
                <img
                  src={avatarDraft}
                  alt=""
                  onError={() => setBroken(true)}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                initialOf(nameDraft || me.name)
              )}
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(0,0,0,.45)",
                  color: C.text,
                  opacity: busy || avatarActive || dragOver ? 1 : 0,
                  transition: "opacity 150ms ease",
                }}
              >
                {busy ? (
                  <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: ".1em" }}>
                    ENVIANDO
                  </span>
                ) : (
                  <CameraIcon size={18} />
                )}
              </span>
            </div>
            <div style={{ paddingBottom: 4 }}>
              <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, lineHeight: 1.6, color: C.textLabel }}>
                SUA FOTO
                <br />
                ARRASTE UMA IMAGEM OU CLIQUE PARA TROCAR
              </div>
              {avatarDraft && (
                <button
                  type="button"
                  onClick={() => setAvatarDraft(null)}
                  className="hov-border"
                  style={{
                    marginTop: 8,
                    padding: "5px 10px",
                    border: `1px solid ${C.lineStrong}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    background: C.field,
                    color: C.textMuted,
                    fontSize: 11.5,
                    fontWeight: 600,
                  }}
                >
                  Remover foto
                </button>
              )}
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = "";
              void receive(file);
            }}
          />

          <div style={{ display: "grid", gap: 14, marginTop: 18 }}>
            <label style={{ display: "grid", gap: 7 }}>
              <span style={MONO_LABEL}>NOME</span>
              <input
                value={nameDraft}
                maxLength={MAX_NAME_LENGTH}
                onChange={event => {
                  setNameDraft(event.target.value);
                  setError(null);
                }}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submit();
                  }
                }}
                style={{
                  ...FIELD,
                  borderColor: problem && error ? hexA(C.red, 0.5) : C.lineStrong,
                }}
              />
            </label>

            <label style={{ display: "grid", gap: 7 }}>
              <span style={{ ...MONO_LABEL, display: "flex", justifyContent: "space-between" }}>
                <span>STATUS</span>
                <span style={{ color: C.textGhost }}>{`${statusDraft.length}/${MAX_STATUS_LENGTH}`}</span>
              </span>
              <input
                value={statusDraft}
                maxLength={MAX_STATUS_LENGTH}
                placeholder="Jogando algo, ouvindo algo…"
                onChange={event => setStatusDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submit();
                  }
                }}
                style={FIELD}
              />
            </label>

            <label style={{ display: "grid", gap: 7 }}>
              <span style={{ ...MONO_LABEL, display: "flex", justifyContent: "space-between" }}>
                <span>BIO</span>
                <span style={{ color: C.textGhost }}>{`${bioDraft.length}/${MAX_BIO_LENGTH}`}</span>
              </span>
              <textarea
                rows={3}
                value={bioDraft}
                maxLength={MAX_BIO_LENGTH}
                placeholder="Conte em poucas linhas quem é você."
                onChange={event => setBioDraft(event.target.value)}
                style={{ ...FIELD, resize: "vertical", fontFamily: FONT_SANS }}
              />
            </label>

            <div>
              <div style={{ ...MONO_LABEL, marginBottom: 8 }}>SUA COR</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {ROLE_SWATCHES.map(swatch => (
                  <button
                    key={swatch}
                    type="button"
                    title={swatch}
                    onClick={() => setColorDraft(swatch)}
                    style={{
                      width: 26,
                      height: 26,
                      padding: 0,
                      borderRadius: 8,
                      cursor: "pointer",
                      background: swatch,
                      border: `2px solid ${swatchRing(colorDraft === swatch)}`,
                    }}
                  />
                ))}
                <button
                  type="button"
                  title="Usar a cor da comunidade"
                  onClick={() => setColorDraft(null)}
                  style={{
                    height: 26,
                    padding: "0 10px",
                    borderRadius: 8,
                    cursor: "pointer",
                    background: hexA(state.settings.accent, 0.18),
                    color: state.settings.accent,
                    border: `2px solid ${swatchRing(colorDraft === null)}`,
                    fontFamily: FONT_MONO,
                    fontSize: 9.5,
                    fontWeight: 600,
                    letterSpacing: ".1em",
                  }}
                >
                  COMUNIDADE
                </button>
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: "8px 12px",
                  border: `1px solid ${hexA(C.red, 0.28)}`,
                  borderRadius: 9,
                  background: hexA(C.red, 0.1),
                  fontSize: 12.5,
                  color: C.red,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                className={busy ? undefined : "hov-bright-108"}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  border: 0,
                  borderRadius: 10,
                  cursor: busy ? "not-allowed" : "pointer",
                  background: "var(--ac, #1F4FD8)",
                  color: C.ink,
                  fontSize: 13.5,
                  fontWeight: 700,
                  opacity: busy ? 0.55 : 1,
                }}
              >
                {busy ? "Enviando imagem…" : "Salvar perfil"}
              </button>
              <button
                type="button"
                onClick={closeProfileEditor}
                className="hov-bg-26"
                style={{
                  padding: "11px 16px",
                  border: `1px solid ${C.lineStrong}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  background: "transparent",
                  color: C.textMuted,
                  fontSize: 13.5,
                  fontWeight: 600,
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
